import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CancellationPolicyRef } from '../../components/legal/CancellationPolicyRef';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { usePatientFlow } from '../../context/PatientFlowContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  FileText, Calendar, Check, Loader2, AlertCircle,
  List, Eye, Clock, CheckCircle, ArrowLeft, Building2,
  Star, MessageCircle, ShieldCheck, Scale, CreditCard
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { formatDateLocale } from '../../utils/dateFormat';
import { PatientOfferTable, splitOfferScopes } from '../../components/patient/PatientOfferTable'; // SALES-CRM-SIMPLIFICATION
import { OfferScopeDetails } from '../../components/patient/OfferScopeDetails';
import { Toaster } from '../../components/Toaster';
import { TranslatedFromEnglishBadge } from '../../components/LanguageBadge';
import api from '../../utils/api';
import { formatMoneyPrecise } from '../../utils/money';
import { PaymentPlanSchedule } from '../../components/payments/PaymentPlanSchedule';
import { useContent } from '../../hooks/useContent';

// TREATX-BATCH-10D — patient web is a strict CONSUMER of the
// backend-authoritative `financial_summary` block. It must never derive
// contractual amounts (total, balance, per-installment) client-side.
// The prior JS mirror `utils/quoteFinancials.js` was removed because it
// constituted a parallel financial authority. The two tiny helpers
// below are purely presentational (currency symbol + defensive coercion
// for legacy responses that pre-date the enrichment). No arithmetic.
const _currencySymbol = (cur) => (
  cur === 'EUR' ? '€' :
  cur === 'USD' ? '$' :
  cur === 'GBP' ? '£' :
  cur === 'TRY' ? '₺' :
  `${cur || ''} `
);
const _fs = (offer) => {
  // If the backend enrichment is present (post-B10D), consume it as-is.
  // If it is absent (pre-B10D legacy cached payload), return an empty
  // shape so the UI falls back to raw fields — NEVER a client
  // recomputation of contractual totals.
  const fs = offer?.financial_summary;
  if (fs && typeof fs === 'object') return fs;
  return {};
};
import { isV3Enabled } from '../../utils/featureFlags';
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug)
import TreatmentReadyPanel from '../../components/treatment-ready/TreatmentReadyPanel';
import ProviderTaxBreakdown from '../../components/ProviderTaxBreakdown';


// Offer Details Modal
// ─── M16B-06A — patient-redacted doctor opinion (advisory) ───
const PatientDoctorOpinionCard = ({ leadId }) => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    if (!leadId) return;
    api.get(`/patient/leads/${leadId}/clinical-reviews`)
      .then(({ data }) => setReviews((data?.reviews || []).filter(r => r.status === 'submitted')))
      .catch(() => setReviews([]));
  }, [leadId]);
  if (!leadId || reviews.length === 0) return null;
  const latest = reviews[0];
  return (
    <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-lg space-y-1" data-testid="patient-doctor-opinion-card">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-emerald-900">Doctor opinion · Review #{latest.review_no}</div>
        <span className="text-[10px] uppercase tracking-wider text-emerald-700">{(latest.recommendation || '').replace(/_/g, ' ')}</span>
      </div>
      {latest.patient_visible_summary && (
        <p className="text-xs text-emerald-900" data-testid="patient-doctor-opinion-summary">{latest.patient_visible_summary}</p>
      )}
      {(latest.recommended_treatments || []).length > 0 && (
        <p className="text-[11px] text-emerald-800">
          <span className="font-medium">Recommended: </span>
          {(latest.recommended_treatments || []).map(t => t.label || t.slug).join(', ')}
        </p>
      )}
      <p className="text-[10px] text-emerald-700/70 italic">Reviewed by {latest.doctor_name}{latest.doctor_specialty ? ` · ${latest.doctor_specialty}` : ''}.</p>
    </div>
  );
};

// ─── M16B-06D — patient revision chain (informational) ───
const PatientRevisionChainCard = ({ leadId, currentOfferId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!leadId) return;
    api.get(`/patient/leads/${leadId}/offer-revisions`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [leadId]);
  if (!data || (data.count || 0) < 2) return null;
  const offers = data.offers || [];
  return (
    <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg space-y-2" data-testid="patient-revision-chain-card">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-blue-900">Offer revision history · {data.count} versions</div>
        <span className="text-[10px] uppercase tracking-wider text-blue-700">latest is v{(offers[offers.length-1]||{}).version_number}</span>
      </div>
      <ul className="space-y-1">
        {offers.map(o => {
          const isCurrent  = o.id === currentOfferId;
          const isCanonical = o.id === data.canonical_offer_id;
          // TREATX-BATCH-1-CANONICAL-SSOT — render canonical total,
          // never raw `base_price`. Backend attaches
          // `financial_summary` on this route; when it is missing
          // (legacy cache) we render `—` rather than fabricating a
          // price from `base_price`.
          const fs = o.financial_summary || {};
          const cur = fs.currency || o.currency || '';
          const total = Number.isFinite(fs.total) ? fs.total : null;
          return (
            <li key={o.id} className={`text-xs flex flex-wrap items-baseline gap-1 ${isCurrent ? 'font-semibold text-blue-900' : 'text-blue-800'}`} data-testid={`patient-revision-row-${o.id}`}>
              <span className="font-mono">v{o.version_number}</span>
              <span>· {o.treatment_name || o.treatment_slug || '—'}</span>
              <span data-testid={`patient-revision-total-${o.id}`}>
                · {total != null ? `${cur} ${total.toLocaleString()}` : '—'}
              </span>
              {o._is_doctor_informed && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-700">· doctor-informed</span>
              )}
              {isCanonical && (
                <span className="text-[10px] uppercase tracking-wider text-blue-900 font-semibold">· canonical</span>
              )}
              {(() => {
                // TREATX-BATCH-1.1 — format canonical price delta on
                // the client using the same currency semantics the
                // Offers List uses. Backend emits numeric fields
                // (`_price_delta`) instead of a preformatted string.
                const pd = o._price_delta;
                if (!pd) return null;
                const prev = `${pd.previous_currency || ''} ${Number(pd.previous_total).toLocaleString()}`.trim();
                const next = `${pd.new_currency || ''} ${Number(pd.new_total).toLocaleString()}`.trim();
                return (
                  <span
                    className="text-[11px] text-blue-700 ml-1"
                    data-testid={`patient-revision-price-delta-${o.id}`}
                  >
                    — price: {prev} → {next}
                  </span>
                );
              })()}
              {(o._changes_from_parent || []).length > 0 && (
                <span className="text-[11px] text-blue-700 ml-1">— {o._changes_from_parent.join(' · ')}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};


const OfferDetailsModal = ({ offer, onClose, onAccept, onReject, canCompare, onCompare, currentLanguage, onMessage }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  if (!offer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" data-testid="offer-details-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold">{offer.treatment || offer.treatment_name}</h2>
              {/* P7Z-3C-C: provider identity row — uses backend-fallback name
                  (no more literal "Clinic" placeholder) plus city / country
                  when present. "Offered by" label clarifies attribution. */}
              <p className="text-blue-100 mt-1 text-sm">
                <span className="text-blue-200/80 mr-1">Offered by</span>
                <span className="font-medium" data-testid="offer-provider-name">
                  {offer.clinic_info?.clinic_name || offer.clinic_name || 'Clinic'}
                </span>
                {(offer.clinic_info?.city || offer.clinic_info?.country) && (
                  <span className="text-blue-100/90" data-testid="offer-provider-location">
                    {' · '}
                    {[offer.clinic_info?.city, offer.clinic_info?.country].filter(Boolean).join(', ')}
                  </span>
                )}
              </p>
              {currentLanguage && currentLanguage !== 'en' && (
                <div className="mt-2">
                  <TranslatedFromEnglishBadge currentLanguage={currentLanguage} />
                </div>
              )}
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          {/* M16B-06A — Doctor opinion (patient-redacted) */}
          <PatientDoctorOpinionCard leadId={offer.lead_id} />

          {/* M16B-06D — Revision chain (if multiple offer versions exist) */}
          <PatientRevisionChainCard leadId={offer.lead_id} currentOfferId={offer.id} />

          {/* Price — TREATX-BATCH-1-CANONICAL-SSOT: patient web is a
              strict consumer of backend `financial_summary`. No
              client-side derivation, no fallback to `base_price` as
              a stand-in for the canonical Total. Base Price is
              rendered as an EXPLICIT priced line item below (owner
              rule: `<Treatment Name> (Base Price)`), and the Total
              here is the canonical sum from the backend. */}
          {(() => {
            const fs = _fs(offer);
            const sym = _currencySymbol(fs.currency || offer.currency);
            const total = Number.isFinite(fs.total) ? fs.total : null;
            const basePrice = Number.isFinite(fs.base_price) ? fs.base_price : null;
            const addTotal = Number.isFinite(fs.additional_services_total) ? fs.additional_services_total : 0;
            const treatmentLabel = (offer.treatment_name || offer.treatment || '').trim();
            const baseLineLabel = treatmentLabel
              ? `${treatmentLabel} (Base Price)`
              : 'Base Price';
            return (
              <div className="space-y-2" data-testid="offer-package-breakdown">
                {/* Base Price as an explicit priced line item */}
                {basePrice != null && (
                  <div
                    className="flex justify-between items-center px-3 py-2 border border-slate-200 rounded-lg bg-white"
                    data-testid="offer-base-price-line-item"
                  >
                    <span className="text-sm text-slate-700" data-testid="offer-base-price-label">
                      {baseLineLabel}
                    </span>
                    <span className="text-sm font-medium text-slate-800" data-testid="offer-base-price-amount">
                      {sym}{basePrice.toLocaleString()}
                    </span>
                  </div>
                )}
                {/* Additional Services subtotal — only when present. The
                    per-service breakdown table lives further down; this
                    is the canonical subtotal that feeds into TOTAL. */}
                {addTotal > 0 && (
                  <div
                    className="flex justify-between items-center px-3 py-2 border border-slate-200 rounded-lg bg-slate-50"
                    data-testid="offer-additional-services-subtotal"
                  >
                    <span className="text-sm text-slate-700">Additional Services Total</span>
                    <span className="text-sm font-medium text-slate-800" data-testid="offer-additional-services-subtotal-amount">
                      {sym}{addTotal.toLocaleString()}
                    </span>
                  </div>
                )}
                {/* TREATX-TAX-5 — provider-declared VAT breakdown */}
                <ProviderTaxBreakdown
                  financialSummary={fs}
                  currencySym={sym}
                  testId="offer-provider-tax"
                />
                {/* Canonical TOTAL (backend-authoritative) */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200" data-testid="offer-total-block">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-800">Total</span>
                    <span className="text-2xl font-bold text-green-700" data-testid="offer-total-amount">
                      {total != null ? `${sym}${total.toLocaleString()}` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ORDER-CONTENT-01 — the patient-visible accepted scope
              (itemised breakdown, included items, additional services) is
              rendered by ONE shared component, so the order screen shows
              the same fields with the same columns as this offer modal.
              TREATX-OWNER-UAT-B10-D2-01 column semantics are preserved
              inside it (Service | Qty | Unit Price | Line Total). */}
          <OfferScopeDetails offer={offer} idBase={offer.id} />

          {/* P7Z-3C-B: "Why this offer?" — provider differentiation snapshot.
              Renders ONLY when the snapshot was captured at offer creation.
              No fallbacks, no fabricated text. */}
          {(() => {
            const ps = offer.provider_snapshot || {};
            const cs = offer.campaign_snapshot || {};
            const tags = (ps.positioning_tags || []).slice(0, 2);
            const vips = (ps.vip_services || []).slice(0, 2);
            const tagDescBy = Object.fromEntries((ps.positioning_tag_details || []).map(d => [d.slug, d.description]));
            const vipDescBy = Object.fromEntries((ps.vip_service_details || []).map(d => [d.slug, d.description]));
            const showCampaign = cs.has_active_campaign === true;
            if (tags.length === 0 && vips.length === 0 && !showCampaign) return null;
            const fmt = (s) => (s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return (
              <div data-testid="patient-why-this-offer">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Why this offer?</h3>
                <ul className="space-y-1">
                  {tags.map(slug => (
                    <li key={`t-${slug}`} className="text-sm text-gray-700">
                      <span className="font-medium">{fmt(slug)}</span>
                      {tagDescBy[slug] && (
                        <span className="block ml-4 text-xs text-gray-500 leading-snug">
                          <span className="text-emerald-600 font-bold">✓</span> {tagDescBy[slug]}
                        </span>
                      )}
                    </li>
                  ))}
                  {vips.map(slug => (
                    <li key={`v-${slug}`} className="text-sm text-gray-700">
                      <span className="font-medium">{fmt(slug)}</span>
                      {vipDescBy[slug] && (
                        <span className="block ml-4 text-xs text-gray-500 leading-snug">
                          <span className="text-emerald-600 font-bold">✓</span> {vipDescBy[slug]}
                        </span>
                      )}
                    </li>
                  ))}
                  {showCampaign && (
                    <li className="text-sm text-rose-700 font-medium" data-testid="patient-why-campaign">
                      🔥{cs.campaign_description ? ` ${cs.campaign_description}` : ' International Patient Package'}
                    </li>
                  )}
                  {/* C4B: frozen offer-level commercial snapshot, sourced from
                      lead.campaign_attribution at offer creation. Trust-first,
                      factual labels — no urgency / scarcity / countdown copy.
                      This is the canonical "what was promised" record on the offer. */}
                  {(() => {
                    const ocs = offer.offer_campaign_snapshot;
                    if (!ocs || !ocs.campaign_id) return null;
                    const lines = [];
                    if (ocs.campaign_title) lines.push(`International Patient Package: ${ocs.campaign_title}`);
                    if (ocs.treatment_type) lines.push(`Treatment: ${fmt(ocs.treatment_type)}`);
                    if (ocs.discount_value != null) {
                      const suffix = ocs.discount_type === 'percentage' ? '%' : '';
                      lines.push(`Package discount: ${ocs.discount_value}${suffix}`);
                    }
                    if (ocs.valid_until) {
                      try {
                        lines.push(`Package valid until: ${formatDateLocale(ocs.valid_until, i18n.language)}`);
                      } catch (_) { /* ignore date parse */ }
                    }
                    if (lines.length === 0) return null;
                    return (
                      <li
                        className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1"
                        data-testid="patient-offer-campaign-snapshot"
                      >
                        <span className="font-semibold block">International Patient Package context (frozen at offer creation)</span>
                        <span className="block text-[11px] text-amber-700">{lines.join(' · ')}</span>
                      </li>
                    );
                  })()}
                </ul>
              </div>
            );
          })()}

          {/* Travel & Accommodation */}
          {(offer.hotel_nights || offer.hotel_type || offer.airport_transfer != null) && (
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Travel & Accommodation</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {offer.hotel_nights && (
                  <div><span className="text-gray-500">Hotel:</span> {offer.hotel_nights} nights{offer.hotel_type ? ` (${offer.hotel_type})` : ''}</div>
                )}
                {offer.airport_transfer != null && (
                  <div><span className="text-gray-500">Airport Transfer:</span> {offer.airport_transfer ? 'Included' : 'Not included'}</div>
                )}
              </div>
              {offer.proposed_dates && (
                <p className="text-sm text-gray-600 mt-1"><span className="text-gray-500">Proposed Dates:</span> {offer.proposed_dates}</p>
              )}
            </div>
          )}

          {/* Payment Details — TREATX-BATCH-1-CANONICAL-SSOT: patient
              web renders the backend-authoritative `financial_summary`
              (deposit, balance, per-installment amount). No client-side
              derivation and no fallback to raw quote fields for
              contractual amounts. Any provider-authored prose in T&C
              that disagrees is commentary, never system truth. */}
          {offer.deposit_amount != null && (() => {
            const fs = _fs(offer);
            return (
              <div data-testid="offer-payment-details">
                <h3 className="font-semibold text-sm text-gray-700 mb-1">
                  {t('patient.offers.paymentDetails', 'Payment Details')}
                </h3>
                {/* TREATX-PLAN-1 — exact ordered collections from the backend
                    derivation; no client-side split, no "× each" claim. */}
                <PaymentPlanSchedule
                  summary={fs}
                  currency={offer.currency}
                  depositType={offer.deposit_type}
                  depositValue={offer.deposit_value}
                  testId="offer-payment-plan"
                />
              </div>
            );
          })()}

          {/* Notes */}
          {offer.notes_patient_visible && (
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-1">Notes from Clinic</h3>
              <p className="text-sm text-gray-600">{offer.notes_patient_visible}</p>
            </div>
          )}

          {/* Terms & Conditions (BATCH-10B): provider-authored, offer-specific.
              If empty, the section is OMITTED entirely — no fallback to CMS. */}
          {offer.terms && String(offer.terms).trim() && (
            <div data-testid="offer-terms-and-conditions">
              <h3 className="font-semibold text-sm text-gray-700 mb-1">
                {t('patient.offers.termsAndConditions', 'Terms & Conditions')}
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{offer.terms}</p>
            </div>
          )}

          {/* Cancellation & Refund Policy (BATCH-10B): rendered from the
              immutable snapshot frozen at offer issuance. NEVER dynamically
              resolved from the live CMS — legacy quotes without a snapshot
              render a neutral notice instead of a fabricated policy. */}
          <div data-testid="offer-cancellation-policy">
            <h3 className="font-semibold text-sm text-gray-700 mb-1">
              {t('patient.offers.cancellationPolicy', 'Cancellation & Refund Policy')}
            </h3>
            <CancellationPolicyRef
              snapshot={offer.cancellation_policy_snapshot}
              language={currentLanguage}
              testId="offer-cancellation-policy-ref"
            />
          </div>

          {/* Valid Until */}
          {offer.valid_until && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              <span>Valid until: {formatDateLocale(offer.valid_until, i18n.language)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 flex gap-3 flex-wrap">
          {canCompare && (
            <Button
              variant="outline"
              onClick={onCompare}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="modal-compare-offers-btn"
            >
              <Scale className="h-4 w-4 mr-1" /> {t('patient.offers.compareSelected')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onMessage && onMessage(offer)}
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            data-testid="modal-message-clinic-btn"
          >
            <MessageCircle className="h-4 w-4 mr-1" /> {t('patient.dashboard.openMessages')}
          </Button>
          {/* V3: Deposit CTA (only entry point for payment) */}
          {isV3Enabled() && (offer.deposit_required || offer.deposit_amount > 0) && !offer.is_selected && offer.status === 'ACCEPTED' && (
            <Button
              onClick={() => navigate(`/patient/payment?quote_id=${offer.id}`)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="v3-modal-deposit-cta"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Pay Deposit ({formatMoneyPrecise(offer.deposit_amount || 0, offer.currency || '')})
            </Button>
          )}
          {/* V3: Selected indicator */}
          {isV3Enabled() && offer.is_selected && (
            <Badge className="bg-green-100 text-green-800 border-green-300 py-2" data-testid="v3-modal-selected-badge">
              <CheckCircle className="h-4 w-4 mr-1" /> Selected
            </Badge>
          )}
          {offer.status === 'SENT' || offer.status === 'VIEWED' ? (
            <>
              <Button
                variant="outline"
                onClick={() => onReject(offer.id)}
                className="flex-1"
                data-testid="reject-offer-button"
              >
                {t('patient.offers.declineOffer')}
              </Button>
              <Button
                onClick={() => onAccept(offer.id)}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="accept-offer-button"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('patient.offers.acceptOffer')}
              </Button>
            </>
          ) : offer.status === 'ACCEPTED' ? (
            <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="modal-accepted-badge">{t('patient.offers.accepted')}</Badge>
          ) : offer.status === 'REJECTED' ? (
            <Badge className="bg-slate-100 text-slate-500 border-slate-300" data-testid="modal-rejected-badge">{t('patient.offers.declined')}</Badge>
          ) : null}
          <Button variant="ghost" onClick={onClose}>{t('patient.common.close')}</Button>
        </div>
      </div>
    </div>
  );
};

const MyOffers = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateFlowState } = usePatientFlow();

  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [serverGroups, setServerGroups] = useState([]); // LIFECYCLE-V3.2
  // SALES-CRM-SIMPLIFICATION — Active/History split uses ONLY the V3.2 server projection (offer_groups)
  const scopes = useMemo(() => splitOfferScopes(quotes, serverGroups), [quotes, serverGroups]);
  const activeOffers = scopes.active;
  const historyOffers = scopes.history;
  const [selectedOfferDetails, setSelectedOfferDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAcceptQuoteId, setConfirmAcceptQuoteId] = useState(null);
  const [confirmRejectQuoteId, setConfirmRejectQuoteId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // CMS content
  const { content: disclaimerContent } = useContent('verified_offers_disclaimer', { flow: 'offer_acceptance' });
  const { content: acceptWarningContent } = useContent('offer_accept_warning_text', { flow: 'offer_acceptance' });

  // Compute leads that have >=2 quotes (compareable)
  const comparableLeads = useMemo(() => {
    const byLead = {};
    for (const q of quotes) {
      if (!q.lead_id) continue;
      if (!byLead[q.lead_id]) byLead[q.lead_id] = 0;
      byLead[q.lead_id]++;
    }
    return new Set(Object.entries(byLead).filter(([, c]) => c >= 2).map(([lid]) => lid));
  }, [quotes]);

  const fetchQuotes = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const queryId = user.person_id || user.id;
      const response = await api.get(`/patient/offers/${queryId}`);

      const data = response.data;
      // Show quotes with visible statuses (including EXPIRED for transparency)
      const visibleQuotes = (data.quotes || []).filter(q =>
        ['SENT', 'VIEWED', 'ACCEPTED', 'EXPIRED', 'REJECTED'].includes(q.status)
      );
      setQuotes(visibleQuotes);
      setServerGroups(data.offer_groups || []); // LIFECYCLE-V3.2
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [user?.person_id, user?.id]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const getAuthHeaders = useCallback(() => {
    const token = '';
    return {
      'Content-Type': 'application/json',
      
    };
  }, []);

  const handleAcceptQuote = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await api.post(`/patient/offers/${quoteId}/accept`, {});
      const data = response.data;
      const autoCount = data.auto_rejected_count || 0;
      const orderId = data?.order?.id;

      toast({
        title: 'Treatment Reserved',
        description: `Your offer is accepted. Next step: payment.${autoCount > 0 ? ` ${autoCount} other offer(s) auto-declined.` : ''}`,
      });
      setSelectedOfferDetails(null);
      setConfirmAcceptQuoteId(null);
      // QOF-03 — hand the patient straight to the payment / next-step screen
      // so the commercial flow does not stall at acceptance.
      const qs = new URLSearchParams();
      qs.set('quote_id', quoteId);
      if (orderId) qs.set('order_id', orderId);
      navigate(`/patient/payment?${qs.toString()}`);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectQuote = async (quoteId, decisionNote) => {
    setActionLoading(quoteId);
    try {
      // Same canonical reject door as PatientQuoteView (alias route → same handler); reason persists as decision_note.
      await api.post(`/patient/offers/${quoteId}/reject`, { reason: decisionNote || '' });

      toast({ title: t('patient.offers.declineSuccess', 'Offer declined'), description: 'The clinic will be notified.' });
      setConfirmRejectQuoteId(null);
      setRejectReason('');
      setSelectedOfferDetails(null);
      await fetchQuotes();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="offers-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('patient.common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12" data-testid="my-offers-page">
      <Toaster />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/patient/dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('adminNav.dashboard')}
            </Button>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">{t('patient.offers.title')}</h1>
          <p className="text-blue-100">
            {t('patient.offers.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Security Notice */}
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3" data-testid="verified-offers-notice">
          <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800">
            {disclaimerContent?.body || 'Loading...'}
          </p>
        </div>

        {/* Offers Content */}
        {quotes.length === 0 ? (
          <Card data-testid="no-offers-empty-state">
            <CardContent className="py-16 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('patient.offers.empty').split(' — ')[0]}</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {t('patient.offers.empty')}
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/patient/clinic-search')}>
                  {t('patient.dashboard.browseTreatments')}
                </Button>
                <Button onClick={() => navigate('/patient/dashboard')}>
                  {t('patient.shell.goToDashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="offers-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" data-testid="offers-count-title">
                <List className="h-4 w-4" />
                {showHistory
                  ? t('patient.offers.historyCount', { count: historyOffers.length })
                  : t('patient.offers.activeCount', { count: activeOffers.length })}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <PatientOfferTable
                quotes={quotes}
                groups={serverGroups}
                showHistory={showHistory}
                onShowHistory={setShowHistory}
                showValidUntil
                showVersion
                renderActions={(quote) => (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedOfferDetails(quote)} title={t('patient.common.viewDetails')} data-testid={`view-offer-${quote.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    {comparableLeads.has(quote.lead_id) && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/offers/compare/${quote.lead_id}`)} title="Compare offers for this lead" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50" data-testid={`compare-offers-${quote.lead_id}`}>
                        <Scale className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => navigate('/patient/messages', { state: { clinicId: quote.clinic_id, subject: `Offer: ${quote.treatment_name || 'Treatment'}`, contextId: quote.id, contextType: 'offer' } })} title="Message Clinic" className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50" data-testid={`message-clinic-${quote.id}`}>
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    {/* Accept only when the server says this exact row is actionable (current version, open, not expired) */}
                    {quote.quote_actionable === true && !showHistory && (
                      <Button size="sm" onClick={() => setConfirmAcceptQuoteId(quote.id)} className="bg-green-600 hover:bg-green-700 text-xs" disabled={actionLoading === quote.id} data-testid={`accept-offer-${quote.id}`}>
                        {actionLoading === quote.id ? <Loader2 className="h-3 w-3 animate-spin" /> : t('patient.offers.acceptOffer')}
                      </Button>
                    )}
                    {quote.status === 'EXPIRED' && quote.is_current !== false && (
                      <span className="text-[10px] text-red-500" data-testid={`expired-hint-${quote.id}`}>{t('patient.offers.requestQuote')}</span>
                    )}
                  </>
                )}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Offer Details Modal */}
      {selectedOfferDetails && (
        <OfferDetailsModal
          offer={selectedOfferDetails}
          onClose={() => setSelectedOfferDetails(null)}
          onAccept={(id) => setConfirmAcceptQuoteId(id)}
          onReject={(id) => setConfirmRejectQuoteId(id)}
          canCompare={comparableLeads.has(selectedOfferDetails.lead_id)}
          onCompare={() => { setSelectedOfferDetails(null); navigate(`/offers/compare/${selectedOfferDetails.lead_id}`); }}
          currentLanguage={language}
          onMessage={(offer) => {
            setSelectedOfferDetails(null);
            navigate('/patient/messages', { state: { clinicId: offer.clinic_id, subject: `Offer: ${offer.treatment_name || 'Treatment'}`, contextId: offer.id, contextType: 'offer' } });
          }}
        />
      )}

      {/* Accept Confirmation Modal */}
      {/* Decline confirmation with reason — same canonical reject door as PatientQuoteView */}
      {confirmRejectQuoteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="reject-confirm-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-[#122033]">{t('patient.offers.declineConfirm', 'Are you sure you want to decline this offer?')}</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('patient.offers.declineReasonPlaceholder', 'Reason (optional) — helps the clinic follow up')}
              className="w-full border rounded-lg p-3 text-sm min-h-[90px]"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { setConfirmRejectQuoteId(null); setRejectReason(''); }} data-testid="reject-confirm-cancel">
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleRejectQuote(confirmRejectQuoteId, rejectReason)}
                disabled={actionLoading === confirmRejectQuoteId}
                data-testid="reject-confirm-yes"
              >
                {actionLoading === confirmRejectQuoteId ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {t('patient.offers.declineOffer', 'Decline')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmAcceptQuoteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="accept-confirm-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#122033]">Accept this offer?</h3>
            </div>
            <p className="text-sm text-slate-500">
              {acceptWarningContent?.body || 'Loading...'}
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmAcceptQuoteId(null)}
                data-testid="accept-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleAcceptQuote(confirmAcceptQuoteId)}
                disabled={actionLoading === confirmAcceptQuoteId}
                data-testid="accept-confirm-yes"
              >
                {actionLoading === confirmAcceptQuoteId ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-1" />
                )}
                Yes, Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOffers;
